import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Plus, Receipt, ArrowLeft, ImageIcon, Clock, CheckCircle2, XCircle,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button, Card, Pill, Spinner, EmptyState } from "@/components/ui";

// =============================================================================
// MyReceiptsScreen — /receipts
// Employee's own submitted receipts, most recent first. Thumbnail + status
// pill + submitted-ago. Big Scan receipt button up top for re-entry.
// T-033 Commit A.
// =============================================================================

const BUCKET = "receipt-captures";
const SIGNED_URL_TTL = 60 * 60;

interface ReceiptRow {
  receipt_id:     string;
  storage_path:   string;
  mime_type:      string | null;
  supplier_guess: string | null;
  amount_guess:   number | null;
  notes:          string | null;
  status:         string;
  captured_at:    string;
  rejection_reason: string | null;
  promoted_to_ap_invoice_id: string | null;
}

// T-374 — once a receipt is coded into AP we follow the bill so the guys can
// see a personal-card spend all the way to "paid back to you".
interface ApFollow {
  status: string;                 // captured/approved | entered_in_xero | paid
  paid_date: string | null;
  is_reimbursement: boolean | null;
  amount_inc_gst: number | null;
}

interface ReceiptWithUrl extends ReceiptRow {
  signedUrl: string | null;
  _ap?: ApFollow | null;
}

function timeAgo(iso: string): string {
  const secs = (Date.now() - new Date(iso).getTime()) / 1000;
  if (secs < 60)    return "just now";
  if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function statusTone(s: string): "pending" | "ok" | "critical" | "default" {
  switch (s) {
    case "new":       return "pending";
    case "promoted":  return "ok";
    case "rejected":  return "critical";
    case "cancelled": return "default";
    default:          return "default";
  }
}

function statusLabel(s: string): string {
  // T-374b — plain language for the guys: they don't care about "coded".
  switch (s) {
    case "new":       return "submitted";
    case "promoted":  return "accepted";
    case "rejected":  return "rejected";
    case "cancelled": return "cancelled";
    default:          return s;
  }
}

// -----------------------------------------------------------------------------

type ReceiptFilter = "all" | "personal" | "company" | "pending" | "paid_back" | "rejected";

export default function MyReceiptsScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<ReceiptFilter>("all");   // T-374b — quick filters

  const { data: receipts = [], isLoading, error } = useQuery({
    queryKey: ["my_receipts", user?.employee_id],
    enabled: !!user?.employee_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pending_receipts")
        .select("receipt_id, storage_path, mime_type, supplier_guess, amount_guess, notes, status, captured_at, rejection_reason, promoted_to_ap_invoice_id, claimed_personal")
        .eq("captured_by_employee_id", user!.employee_id!)
        .order("captured_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      const rows = (data ?? []) as ReceiptRow[];
      if (rows.length === 0) return [] as ReceiptWithUrl[];

      const { data: signed } = await supabase
        .storage
        .from(BUCKET)
        .createSignedUrls(rows.map(r => r.storage_path), SIGNED_URL_TTL);
      const urlByPath = new Map((signed ?? []).map(s => [s.path ?? "", s.signedUrl ?? null]));

      // T-374 — follow promoted receipts into AP so a personal-card spend shows
      // its journey: coded → in AP queue → PAID BACK. RLS permitting; if the
      // AP rows aren't visible to this role we degrade gracefully to "coded".
      const apIds = [...new Set(rows.map(r => r.promoted_to_ap_invoice_id).filter(Boolean))] as string[];
      const apMap = new Map<string, ApFollow>();
      if (apIds.length) {
        const { data: aps } = await supabase
          .from("ap_invoices")
          .select("ap_invoice_id, status, paid_date, is_reimbursement, amount_inc_gst")
          .in("ap_invoice_id", apIds);
        for (const a of (aps ?? []) as any[]) {
          apMap.set(a.ap_invoice_id, {
            status: a.status, paid_date: a.paid_date,
            is_reimbursement: a.is_reimbursement, amount_inc_gst: a.amount_inc_gst,
          });
        }
      }
      return rows.map(r => ({
        ...r,
        signedUrl: urlByPath.get(r.storage_path) ?? null,
        _ap: r.promoted_to_ap_invoice_id ? apMap.get(r.promoted_to_ap_invoice_id) ?? null : null,
      }));
    },
  });

  const totals = useMemo(() => {
    let pending = 0, coded = 0, declined = 0, owedToMe = 0, paidBack = 0;
    for (const r of receipts) {
      if (r.status === "new") pending++;
      else if (r.status === "promoted") coded++;
      else if (r.status === "rejected") declined++;
      // T-374 — personal-card money: owed until the reimbursement bill is paid.
      if (r._ap?.is_reimbursement) {
        if (r._ap.status === "paid") paidBack++;
        else owedToMe += Number(r._ap.amount_inc_gst ?? r.amount_guess ?? 0);
      }
    }
    return { pending, coded, declined, owedToMe, paidBack };
  }, [receipts]);

  if (!user) return null;

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto">
      <button
        onClick={() => navigate("/portal")}
        className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3"
        type="button"
      >
        <ArrowLeft size={14} /> Portal
      </button>

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-gradient-to-br from-accent/20 to-accent/5 text-accent flex items-center justify-center border border-accent/30">
            <Receipt size={22} />
          </div>
          <div>
            <h1 className="text-[22px] font-semibold text-text">My receipts</h1>
            <p className="text-[13px] text-text-muted mt-0.5">
              Receipts you've snapped and submitted for coding.
            </p>
          </div>
        </div>
        <Link to="/receipts/new">
          <Button icon={Plus}>Scan receipt</Button>
        </Link>
      </div>

      {/* Summary strip */}
      {receipts.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          <MiniStat label="Pending" value={totals.pending} icon={Clock} tone={totals.pending > 0 ? "warn" : "neutral"} />
          <MiniStat label="Coded"   value={totals.coded}   icon={CheckCircle2} tone={totals.coded > 0 ? "ok" : "neutral"} />
          <MiniStat label="Declined" value={totals.declined} icon={XCircle} tone={totals.declined > 0 ? "danger" : "neutral"} />
        </div>
      )}

      {/* T-374b — quick filters: whose money + where it's at */}
      {receipts.length > 0 && (
        <div className="flex gap-1.5 mb-4 flex-wrap">
          {([
            ["all", "All"],
            ["personal", "My money"],
            ["company", "Company"],
            ["pending", "Waiting"],
            ["paid_back", "Paid back"],
            ["rejected", "Rejected"],
          ] as [ReceiptFilter, string][]).map(([v, label]) => (
            <button key={v} type="button" onClick={() => setFilter(v)}
              className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
                filter === v ? "bg-accent/15 text-accent border-accent/40" : "bg-ink-raised text-text-muted border-line"
              }`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* T-374 — money owed back to you (personal-card receipts) */}
      {totals.owedToMe > 0 && (
        <Card className="mb-4 border-accent/40">
          <div className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-accent/10 text-accent flex items-center justify-center border border-accent/30 flex-shrink-0">
              <Receipt size={16} />
            </div>
            <div className="flex-1">
              <div className="text-[13px] text-text font-medium">
                ${totals.owedToMe.toFixed(2)} owed back to you
              </div>
              <div className="text-[11px] text-text-muted mt-0.5">
                Personal-card spends in the payment queue — each shows "paid back" below once the office pays it.
              </div>
            </div>
          </div>
        </Card>
      )}

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : error ? (
        <Card>
          <div className="p-6 text-[13px] text-red-400">
            Can't load receipts: {error instanceof Error ? error.message : String(error)}
          </div>
        </Card>
      ) : receipts.length === 0 ? (
        <Card>
          <EmptyState
            icon={Receipt}
            title="No receipts yet"
            description="Tap Scan receipt up top when you've bought gear on the road."
          />
        </Card>
      ) : (() => {
        const isPersonal = (r: any) => r._ap?.is_reimbursement || r.claimed_personal;
        const filtered = receipts.filter((r: any) => {
          switch (filter) {
            case "personal":  return isPersonal(r);
            case "company":   return !isPersonal(r);
            case "pending":   return r.status !== "rejected" && !(isPersonal(r) ? r._ap?.status === "paid" : r.status === "promoted");
            case "paid_back": return isPersonal(r) && r._ap?.status === "paid";
            case "rejected":  return r.status === "rejected";
            default:          return true;
          }
        });
        return filtered.length === 0 ? (
          <Card><EmptyState icon={Receipt} title="Nothing here" description="No receipts match that filter." /></Card>
        ) : (
          <Card>
            <div className="divide-y divide-line">
              {filtered.map(r => <ReceiptRowCard key={r.receipt_id} r={r} />)}
            </div>
          </Card>
        );
      })()}
    </div>
  );
}

// -----------------------------------------------------------------------------

function MiniStat({
  label, value, icon: Icon, tone = "neutral",
}: {
  label: string; value: number; icon: typeof Clock;
  tone?: "ok" | "warn" | "danger" | "neutral";
}) {
  const valueColour =
    tone === "danger" ? "text-danger" :
    tone === "warn"   ? "text-warn"   :
    tone === "ok"     ? "text-ok"     :
    "text-text";
  return (
    <Card>
      <div className="p-3">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono">{label}</div>
          <Icon size={12} className="text-text-muted" />
        </div>
        <div className={`text-[18px] font-semibold ${valueColour}`}>{value}</div>
      </div>
    </Card>
  );
}

function ReceiptRowCard({ r }: { r: ReceiptWithUrl }) {
  const isPdf = r.mime_type === "application/pdf";
  return (
    <div className="p-3 flex gap-3">
      <div className="w-16 h-16 rounded-md bg-ink border border-line overflow-hidden flex items-center justify-center flex-shrink-0">
        {r.signedUrl && !isPdf ? (
          <img src={r.signedUrl} alt="Receipt" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <ImageIcon size={20} className="text-text-muted" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-medium text-text">
            {r.supplier_guess || <span className="text-text-muted italic">No supplier given</span>}
          </span>
          {/* T-374b — reimbursement journey from the moment of SUBMISSION
              (worker's own declaration), not just once the office codes it. */}
          {r._ap?.is_reimbursement || (r as any).claimed_personal ? (
            r.status === "rejected" ? (
              <Pill label="rejected" tone="critical" small />
            ) : r._ap?.status === "paid" ? (
              <Pill label={`paid back${r._ap.paid_date ? ` ${new Date(r._ap.paid_date + "T00:00:00").toLocaleDateString()}` : ""}`} tone="ok" small />
            ) : r._ap ? (
              <Pill label="reimbursement — owed to you" tone="pending" small />
            ) : (
              <Pill label="reimbursement — submitted" tone="pending" small />
            )
          ) : (
            <Pill label={statusLabel(r.status)} tone={statusTone(r.status)} small />
          )}
        </div>
        <div className="text-[12px] text-text-muted mt-0.5 font-mono">
          {r.amount_guess != null ? `$${Number(r.amount_guess).toFixed(2)}` : "—"} · {timeAgo(r.captured_at)}
        </div>
        {r.notes && (
          <div className="text-[12px] text-text mt-1 line-clamp-2">{r.notes}</div>
        )}
        {r.status === "rejected" && r.rejection_reason && (
          <div className="mt-1.5 p-2 rounded bg-danger/10 border border-danger/30 text-[11px] text-danger">
            Declined: {r.rejection_reason}
          </div>
        )}
      </div>
    </div>
  );
}
